import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, Download, Info, Loader2, ShieldAlert, X } from 'lucide-react';
import { externalAccess, ApiError } from '@/api/client';
import { errorText, errorToneClass, isPermissionError } from '@/lib/permissionError';

/**
 * Sprint-18 (eISF spec §6 + permission matrix) — external-reviewer
 * document preview modal.
 *
 * Simpler than the internal viewer — no check-out/in, no metadata edit,
 * no tags. Just inline PDF preview + download. Server refuses the URL
 * for PHI documents under view_pii_redacted scope; in that case we
 * surface a 403 message clearly.
 */

interface Props {
  binderId: string;
  documentId: string;
  sessionToken: string;
  open: boolean;
  onClose: () => void;
}

export function ExternalDocumentViewerModal({
  binderId, documentId, sessionToken, open, onClose,
}: Props) {
  const q = useQuery({
    queryKey: ['external-doc-preview', binderId, documentId, sessionToken],
    queryFn: () => externalAccess.getDocumentPreview(binderId, documentId, sessionToken),
    enabled: open,
    retry: false,
  });

  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, onClose]);

  if (!open) return null;

  const phiBlock = q.error instanceof ApiError && q.error.status === 403;
  const data = q.data;

  return (
    <div className="fixed inset-0 z-[60] bg-ink-primary/40 flex items-start justify-center" onClick={onClose}>
      <div
        className="bg-white mt-12 w-[1300px] max-w-[calc(100vw-50px)] h-[calc(100vh-100px)] rounded-md shadow-2xl flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-center gap-3 px-5 py-3 border-b border-surface-border">
          <button type="button" onClick={onClose} className="p-1.5 text-ink-muted hover:text-ink-primary">
            <X className="h-5 w-5" />
          </button>
          <div className="text-base text-ink-primary truncate">
            {data ? (
              <>
                <span className="text-ink-muted">{data.document.folder_breadcrumb} /</span>{' '}
                <span className="font-medium">{data.document.title}</span>
                {data.document.phi_present && (
                  <span className="ml-2 inline-flex items-center gap-1 rounded bg-amber-50 px-1.5 py-0.5 text-[11px] text-amber-800">
                    <ShieldAlert className="h-3 w-3" /> PHI
                  </span>
                )}
              </>
            ) : 'Loading…'}
          </div>
        </header>

        <div className="flex-1 bg-surface-muted/30 flex items-center justify-center overflow-hidden">
          {q.isLoading ? (
            <div className="flex items-center gap-2 text-ink-muted">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading document…
            </div>
          ) : phiBlock ? (
            <div className="text-center max-w-md">
              <ShieldAlert className="h-10 w-10 text-amber-600 mx-auto" />
              <div className="mt-3 text-base font-medium text-ink-primary">PHI-protected document</div>
              <div className="mt-2 text-sm text-ink-muted">
                This document contains PHI and is not viewable under your
                access scope. Contact the site to request an extended
                access grant if you require access.
              </div>
            </div>
          ) : q.isError ? (
            <div className={`text-center max-w-md text-sm ${errorToneClass(q.error)}`}>
              {isPermissionError(q.error) ? <Info className="h-6 w-6 mx-auto"/> : <AlertTriangle className="h-6 w-6 mx-auto" />}
              <div className="mt-2">{errorText(q.error, 'Failed to load document.')}</div>
            </div>
          ) : data?.preview_url ? (
            <iframe
              key={data.preview_url}
              src={`${data.preview_url}#toolbar=1&navpanes=0&statusbar=0`}
              title={data.version.file_name}
              className="w-full h-full bg-white border-0"
            />
          ) : (
            <div className="text-center text-sm text-ink-muted">
              <Download className="h-8 w-8 mx-auto" />
              <div className="mt-2">This file type isn&rsquo;t previewable inline.</div>
              {data && (
                <a
                  href={data.presigned_get_url}
                  download={data.version.file_name}
                  className="mt-3 inline-flex items-center gap-1.5 rounded bg-ink-primary px-3 h-9 text-sm font-medium text-white hover:bg-ink-primary/90"
                >
                  <Download className="h-3.5 w-3.5" /> Download
                </a>
              )}
            </div>
          )}
        </div>

        <footer className="px-5 py-3 border-t border-surface-border flex items-center justify-end gap-2 bg-white">
          {data && (
            <a
              href={data.presigned_get_url}
              download={data.version.file_name}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 rounded border border-surface-border bg-white px-3.5 h-9 text-sm font-medium text-ink-primary hover:bg-surface-muted/50"
            >
              <Download className="h-3.5 w-3.5" /> Download
            </a>
          )}
          <button
            type="button"
            onClick={onClose}
            className="inline-flex items-center rounded bg-ink-primary px-4 h-9 text-sm font-medium text-white hover:bg-ink-primary/90"
          >
            Close
          </button>
        </footer>
      </div>
    </div>
  );
}
